import _ from 'lodash';
import React from 'react';

import './style.css';

import LeftArrowIcon from '../../assets/icons/left-arrow.png';
import { useRedirect } from '../../components/Router/Router';
import { contactsRoute } from '../../routes';
import { GameCard } from './components/GameCard/GameCard';
import { calls } from '../../content';
import { Call } from '../../call';
import { useSound } from '../../components/AudioProvider/useSound';

/*
    3: min=6 -> 6=2*3
    4: min=8 -> 12=3*4
    5: min=10 -> 12=3*4
    6: min=12 -> 20=3*5
    7: min=14 -> 24=4*6
*/
const generateCardDistribution = (cards: number, pairs: number) => {
    const sequence = new Array(cards)
        .fill(undefined)
        .map((_, i) => i);

    if (cards > pairs) {
        return _.shuffle(_.shuffle(sequence).slice(0, pairs).flatMap(x => [x, x]));
    }

    const random = new Array(pairs - cards)
        .fill(undefined)
        .map(() => Math.floor(Math.random() * cards));

    return _.shuffle([
        ...sequence,
        ...sequence,
        ...random,
        ...random,
    ]);
};

interface Card {
    call: Call;
    open: boolean;
}

const useMemoryGame = (pairs: number, session: number) => {
    const [cards, setCards] = React.useState<Card[]>([]);

    React.useEffect(() => {
        const availableCalls = calls.filter(({ available }) => available);
        const distribution = generateCardDistribution(availableCalls.length, pairs);

        setCards(cards => distribution.map((card, i) => ({
            call: cards[i] ? cards[i].call : availableCalls[card],
            open: false,
        })));

        const delay = session === 0 ? 0 : 500;

        const timeout = setTimeout(() => {
            setCards(distribution.map(index => ({
                call: availableCalls[index],
                open: false,
            })));
        }, delay);

        return () => {
            clearTimeout(timeout);
        };
    }, [session, pairs]);

    const updateCard = React.useCallback((index: number, open: boolean) => {
        setCards(cards => cards.map((card, i) => index === i ? { ...card, open } : card));
    }, []);

    return [cards, updateCard] as const;
};

const useCallSounds = () => {
    // похуй
    return calls.map(call => useSound(call.audio)[0]);
};

export interface MemoryGameProps { }

type Difficulty = 1 | 2 | 3 | 4;
const pairs = {
    1: 3,
    2: 6,
    3: 12,
    4: 30,
} as const;

const fieldSize = {
    [1]: '2x3',
    [2]: '3x4',
    [3]: '4x6',
    [4]: '6x10',
} as const;

export const MemoryGame: React.FC<MemoryGameProps> = () => {
    const redirect = useRedirect();

    const goToContactsPage = React.useCallback(() => {
        redirect(contactsRoute({}));
    }, []);

    const [session, newSession] = React.useReducer(x => x + 1, 0);
    const [difficulty, setDifficulty] = React.useState<Difficulty>(1);
    const [cards, updateCard] = useMemoryGame(pairs[difficulty], session);
    const [first, setFirst] = React.useState<number | null>(null);
    const [second, setSecond] = React.useState<number | null>(null);
    const pair = [first, second];

    const restart = (difficulty?: Difficulty) => {
        if (difficulty !== undefined) {
            setDifficulty(difficulty);
        }
        newSession();
        setFirst(null);
        setSecond(null);
    };

    const soundFns = useCallSounds();

    React.useEffect(() => {
        if (first !== null && second !== null) {
            if (cards[first].call === cards[second].call) {
                updateCard(first, true);
                updateCard(second, true);
                setFirst(null);
                setSecond(null);

                soundFns[calls.findIndex(call => call === cards[first].call)]();
            } else {
                const timeout = setTimeout(() => {
                    setFirst(null);
                    setSecond(null);
                }, 1000);

                return () => clearTimeout(timeout);
            }
        }
    }, [first, second]);

    return <div className='page__root'>
        <div className="page__header">
            <div className='page__header__button page__header__button--left' onClick={goToContactsPage}>
                <img src={LeftArrowIcon} className='page__header__button--back' />
            </div>
            <div className='page__header__title'>Найди Рому</div>
            <div className='page__header__button page__header__button--right' />
        </div>
        <div className='game-settings'>
            <div className={'game-settings__button' + (difficulty === 1 ? ' selected' : '')} onClick={() => restart(1)}>Легко</div>
            <div className={'game-settings__button' + (difficulty === 2 ? ' selected' : '')} onClick={() => restart(2)}>Нормально</div>
            <div className={'game-settings__button' + (difficulty === 3 ? ' selected' : '')} onClick={() => restart(3)}>Сложно</div>
            <div className={'game-settings__button' + (difficulty === 4 ? ' selected' : '')} onClick={() => restart(4)}>Нереально</div>
        </div>

        <div className="field-container">
            <div className={`game__field game__field--${fieldSize[difficulty]}`}>
                {cards.map((card, index) => (
                    <GameCard
                        image={card.call.image}
                        key={index}
                        flipped={!(card.open || pair.includes(index))}
                        onClick={() => {
                            if (first !== null && second !== null) {
                                return;
                            }

                            if (card.open) {
                                return;
                            }

                            if (first === null) {
                                setFirst(index);
                            } else if (first === index) {
                                setFirst(null);
                            } else {
                                setSecond(index);
                            }
                        }}
                    />
                ))}
            </div>
        </div>

        <div className='game-settings'>
            <div className='game-settings__button' onClick={() => restart(difficulty)}>Заново</div>
        </div>
    </div>;
};
